import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { NumberInput } from "../../ui/number-input";
import ComponentSubRow from "../../lineItems/rows/ComponentSubRow";
import CustomFooterRow from "../../lineItems/rows/CustomFooterRow";
import {
  formatCurrency,
  formatThicknessInches,
} from "../../../utils/formatters";
import {
  buildMaterialClassificationOptions,
  FINISH_MODE_OPTIONS,
  getFinishModeHelper,
  inferMaterialClassification,
  isTypedLine,
} from "../../../utils/materialFlow";
import Disclosure from "../Disclosure";
import LaborOverridePanel from "./LaborOverridePanel";
import ChildComponentsPanel from "./ChildComponentsPanel";
import RafterTailTrialPanel from "./RafterTailTrialPanel";
import { childDisplayName } from "./childModel";
import {
  RAFTER_TAIL_TRIAL_MODEL,
  RATE_CARD_MODEL_LABEL,
  TRIAL_FINISH_OPTIONS,
  TRIAL_MODEL_LABEL,
  canUseRafterTailTrial,
  isRafterTailTrialLine,
  trialFinishSelected,
} from "./rafterTailModel";
import {
  AI_CANDIDATE_FAILED,
  AI_CANDIDATE_FILED,
  AI_DRAFT_BADGE,
  DEPARTMENT_LABELS,
  DEPARTMENT_ORDER,
  LABOR_BASIS_WORDS,
  MATERIAL_FAILURE_HEADLINE,
  QUOTED_PRICE_FILED,
  QUOTED_PRICE_FILE_FAILED,
  QUOTED_PRICE_FILE_HELPER,
  QUOTED_PRICE_HELPER,
  QUOTED_PRICE_NOT_USED_SUFFIX,
  CATALOG_RATE_LEAD,
  STANDALONE_QUOTED_LINE_BADGE,
  STANDALONE_QUOTED_LINE_HINT,
  STANDALONE_QUOTED_LINE_NOTE,
  aiDraftExplainer,
  describeEngine,
  describeGeometry,
  describeMaterialResolution,
  laborOverrideApplied,
} from "./traceLabels";
import { isAiDrafted, isAiDraftPending } from "./composerModel";
import { pricedAsStandaloneQuotedLine } from "./quotedLine";
import {
  MATERIAL_PRICE_OVERRIDE_BASIS_OPTIONS,
  MATERIAL_PRICE_OVERRIDE_BASIS_LABELS,
} from "../../../state/lineItems";
import { describeLineError, describeMaterialError } from "./lineErrors";
import {
  getEngineLineTotal,
  getItemQuantity,
  getLineDisplayName,
  getLineLaborHours,
  toNumberOrNull,
} from "./qboLine";
import { describeRateConfirmation } from "./traceModel";

/**
 * The centre pane: one line item, laid out as labelled fieldsets instead of
 * table cells.
 *
 * Every control dispatches exactly the action the table row dispatched, with
 * the same field names — the reducer's clearing rules (product type wipes the
 * unused dimensions, material class drops a pinned sheet, waste mode drops a
 * stale custom percent) are unchanged and un-duplicated here.
 *
 * Custom Mode is NOT rewritten in this pane: its component rows render through
 * the existing ComponentSubRow / CustomFooterRow, under the same column
 * geometry they were built for.
 */

const WASTE_MODE_OPTIONS = [
  { value: "included", label: "Waste already included" },
  { value: "default", label: "Add default" },
  { value: "custom", label: "Custom %" },
];

const DEFAULT_WASTE_PCT = 15;
const MATERIAL_FAILURE_CANDIDATE_LIMIT = 5;

function stripInchQuotes(value) {
  return String(value).replace(/\\?["″]/g, "");
}

function nameAlreadyIncludesThickness(name, formattedThickness) {
  if (!name || !formattedThickness) return false;
  const withoutQuotes = stripInchQuotes(formattedThickness);
  return (
    name.includes(formattedThickness) ||
    (Boolean(withoutQuotes) && stripInchQuotes(name).includes(withoutQuotes))
  );
}

function pricedWithMaterialLabel(resolution) {
  const name = resolution.material_name || resolution.material_class;
  const thickness = formatThicknessInches(resolution.thickness_in);
  const parts = nameAlreadyIncludesThickness(name, thickness)
    ? [name]
    : [name, thickness];
  return parts.filter(Boolean).join(" ") || "no material yet";
}

function Derived({ items }) {
  const shown = items.filter((entry) => entry.value !== null);
  if (!shown.length) return null;
  return (
    <div className="est-derived">
      {shown.map((entry) => (
        <div key={entry.label}>
          <span className="est-derived__k">{entry.label}</span>
          <div className="est-derived__v wb-num">{entry.value}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * What a Product → Custom switch would set aside on this line.
 *
 * Custom Mode prices from hand-entered components instead of the product type,
 * so a line that already has a type, dimensions or a priced result silently
 * changes price the moment the toggle is pressed. Naming the pieces is what
 * makes the confirmation worth reading.
 */
export function describeCustomSwitchLosses(lineItem, result) {
  if (!lineItem) return [];
  const dimensions = ["length", "width", "height"]
    .map((key) => toNumberOrNull(lineItem[key]))
    .filter((value) => value !== null && value > 0);

  return [
    lineItem.product_type_key ? "the product type on this line" : null,
    dimensions.length ? "its dimensions" : null,
    lineItem.material_classification ? "its material class" : null,
    toNumberOrNull(result?.calculated_total_cost) !== null
      ? "the price the rate card produced"
      : null,
  ].filter(Boolean);
}

/**
 * The review bar on an AI-drafted line that has not been accepted yet.
 *
 * Three things make a drafted line safe to have in a quoting tool, and all
 * three live here: the line is visibly marked, every field under this bar is
 * editable exactly as a hand-typed line is, and the line is excluded from the
 * calculation payload entirely while this bar is showing (see
 * buildCalculationPayload) — a pending draft is never priced, by any route.
 * Accepting it hands the line to the normal pricing path, where it behaves
 * like any hand edit.
 *
 * The badge is accent-coloured, never amber: amber means Custom Mode and
 * --wb-warn means a hand override. Three meanings, three colours.
 */
export function AiDraftBar({
  draft = null,
  onAccept,
  onDiscard,
  candidateState = null,
  onRetryCandidate,
}) {
  const evidenceCount = draft?.evidence?.length || 0;

  return (
    <div className="est-draftbar">
      <div className="est-draftbar__hd">
        <span className="est-draftbar__badge">{AI_DRAFT_BADGE}</span>
        <p className="est-draftbar__lead">{aiDraftExplainer(evidenceCount)}</p>
      </div>
      <div className="est-draftbar__actions">
        <button type="button" className="wb-tbtn" onClick={onDiscard}>
          Discard
        </button>
        <button
          type="button"
          className="wb-tbtn wb-tbtn--solid"
          onClick={onAccept}
        >
          Accept line
        </button>
      </div>
      {candidateState === "filed" ? (
        <p className="est-draftbar__note" role="status">
          {AI_CANDIDATE_FILED}
        </p>
      ) : null}
      {candidateState === "failed" ? (
        <p
          className="est-draftbar__note est-draftbar__note--warn"
          role="status"
        >
          {AI_CANDIDATE_FAILED}{" "}
          <button
            type="button"
            className="wb-plain est-draftbar__retry"
            onClick={onRetryCandidate}
          >
            Retry
          </button>
        </p>
      ) : null}
    </div>
  );
}

function LineItemEditor({
  lineItem,
  lineIndex,
  dispatch,
  availableProductTypes = [],
  availableMaterials = [],
  result = null,
  formData = {},
  laborPanelRef,
  isAdmin = false,
  draft = null,
  onAcceptDraft,
  onDiscardDraft,
  candidateState = null,
  onRetryCandidate,
  onFileQuotedPrice,
  quotedPriceFileState = null,
}) {
  const isCustom = lineItem?.mode === "custom";
  const isTyped = isTypedLine(lineItem || {});
  const isRetiredCatalogLine = Boolean(lineItem?.product_id) && !isTyped;
  // A rafter tail on the pricing trial is costed by its own rules: no sheet
  // pick, no quoted price, no rate card, no labor overrides, no components.
  const isTrial = isRafterTailTrialLine(lineItem);
  const canTrial = canUseRafterTailTrial(lineItem);
  const [confirmCustom, setConfirmCustom] = useState(false);

  const productType = useMemo(
    () =>
      (availableProductTypes || []).find(
        (type) => type.key === lineItem?.product_type_key,
      ) || null,
    [availableProductTypes, lineItem?.product_type_key],
  );

  const allClassOptions = useMemo(
    () => buildMaterialClassificationOptions(availableMaterials || []),
    [availableMaterials],
  );

  const classOptions = useMemo(() => {
    const compatible = productType?.compatible_material_classes;
    if (!Array.isArray(compatible) || compatible.length === 0) {
      return allClassOptions;
    }
    return allClassOptions.filter((option) =>
      compatible.includes(option.value),
    );
  }, [allClassOptions, productType]);

  const sheetOptions = useMemo(() => {
    const targetClass = lineItem?.material_classification || "";
    return (availableMaterials || []).filter((material) => {
      if (!targetClass) return true;
      return inferMaterialClassification(material?.name) === targetClass;
    });
  }, [availableMaterials, lineItem?.material_classification]);

  if (!lineItem) {
    return (
      <p className="wb-empty">Add a line item to start pricing this job.</p>
    );
  }

  const update = (field, value) =>
    dispatch({
      type: "UPDATE_LINE_ITEM_FIELD",
      index: lineIndex,
      field,
      value,
    });

  const requiredDims =
    isTyped && Array.isArray(productType?.required_dimensions)
      ? productType.required_dimensions
      : null;
  const showDim = (dim) =>
    !requiredDims || requiredDims.length === 0 || requiredDims.includes(dim);

  const quantity = getItemQuantity({ quantity: lineItem.quantity });
  const sqft = toNumberOrNull(result?.calculated_sqft);
  const band = result?.rate_card_resolved?.band_label || null;
  const resolution = result?.material_resolution || null;
  const promptsForWaste =
    isTyped && productType?.waste_prompt_mode === "prompt";
  const defaultWastePct =
    productType?.default_waste_pct != null
      ? productType.default_waste_pct
      : DEFAULT_WASTE_PCT;
  const finishHelper = getFinishModeHelper(lineItem.finish_mode);

  // A quoted price with no product type is a complete line, not an unfinished
  // one: the engine prices it from the quote plus any hand-entered hours. The
  // product type stops being required the moment such a price is present.
  const isQuotedLine =
    !isCustom && pricedAsStandaloneQuotedLine(result, lineItem);

  // A blank custom assembly is still identifiable by what it is made of —
  // "Custom assembly" on every such line makes two different lines read alike.
  // A typed name wins on either mode — it is what an estimator wrote down to
  // match this line back to a drawing, and it should win over a generic
  // product-type or product name the moment it exists.
  const title = isCustom
    ? lineItem.name ||
      getLineDisplayName(result, lineItem) ||
      "Custom / one-off line"
    : lineItem.name ||
      productType?.display_name ||
      result?.product_name ||
      (isQuotedLine ? STANDALONE_QUOTED_LINE_BADGE : "New line item");

  const subtitle = [
    describeEngine(
      result?.components?.engine_version_used || result?.engine_version_used,
    ),
    describeGeometry(
      result?.components?.geometry_method_used || result?.geometry_method_used,
    ),
    result?.labor_basis_used
      ? `labor by ${LABOR_BASIS_WORDS[result.labor_basis_used] || result.labor_basis_used}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const toggleMode = () =>
    dispatch({ type: "TOGGLE_LINE_ITEM_MODE", index: lineIndex });

  // Losing $1,663 of priced work to a mis-click is not an undoable mistake in a
  // quoting tool, so a line with anything on it asks first.
  const customLosses = describeCustomSwitchLosses(lineItem, result);

  const handleGoCustom = () => {
    if (isCustom) return;
    if (customLosses.length) {
      setConfirmCustom(true);
      return;
    }
    toggleMode();
  };

  const modeToggle = (
    <div className="est-mode-wrap">
      <div className="est-mode" role="group" aria-label="Line type">
        <button
          type="button"
          className={`wb-plain est-mode__btn${!isCustom ? " est-mode__btn--on" : ""}`}
          aria-label="Product"
          aria-pressed={!isCustom}
          title="Use a standard family and rate card"
          onClick={() => isCustom && toggleMode()}
        >
          <span>From product type</span>
        </button>
        <button
          type="button"
          className={`wb-plain est-mode__btn est-mode__btn--custom${
            isCustom ? " est-mode__btn--on" : ""
          }`}
          aria-label="Custom"
          aria-pressed={isCustom}
          title="Price a purchased item or build from parts"
          onClick={handleGoCustom}
        >
          <span>Custom / one-off</span>
        </button>
      </div>
    </div>
  );

  const customConfirmDialog = (
    <Dialog open={confirmCustom} onOpenChange={setConfirmCustom}>
      <DialogContent className="est-confirm">
        <DialogHeader>
          <DialogTitle>Switch to a custom / one-off line?</DialogTitle>
          <DialogDescription>
            A custom line prices from purchased items, materials, and shop
            operations you enter. This line stops using{" "}
            {customLosses.join(", ")}, and its price will change.
          </DialogDescription>
        </DialogHeader>
        <div className="est-confirm__actions">
          <button
            type="button"
            className="wb-tbtn"
            onClick={() => setConfirmCustom(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="wb-tbtn wb-tbtn--solid"
            onClick={() => {
              setConfirmCustom(false);
              toggleMode();
            }}
          >
            Switch line type
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );

  const drafted = isAiDrafted(lineItem);
  const draftPending = isAiDraftPending(lineItem);

  // The eyebrow names what kind of line this is. On an unnamed custom line the
  // H1 falls back to that same phrase, so it would be said twice in a row —
  // it renders only when it says something the H1 does not.
  const eyebrow = drafted
    ? "AI-drafted custom assembly"
    : isCustom
      ? "Custom / one-off line"
      : isQuotedLine
        ? STANDALONE_QUOTED_LINE_BADGE
        : null;

  // A line the backend refused to price contributes $0 to the job. That must
  // be the loudest thing on the screen — it used to be invisible, and the job
  // total looked entirely plausible without it.
  const lineError = result?.error ? describeLineError(result.error) : null;

  // A material failure is a different defect: the line DID price — shop
  // labor still charged — only the material dollars are $0 until a sheet is
  // picked or a Quoted price is entered. This must never read as "did not
  // price".
  const materialError = result?.material_error
    ? describeMaterialError(result.material_error)
    : null;

  const hasOverride =
    lineItem.material_price_override !== null &&
    lineItem.material_price_override !== undefined &&
    lineItem.material_price_override !== "";

  const basisLabel =
    MATERIAL_PRICE_OVERRIDE_BASIS_LABELS[
      lineItem.material_price_override_basis
    ] || null;

  // The closed Quoted-price row has to say whether there is one, and if so how
  // much — the amount exactly as entered, never a computed total. "Each" is
  // the engine's default basis, so a bare amount reads as "$122.00 each".
  const quotedBasis = lineItem.material_price_override_basis;
  const quotedMeta = !hasOverride
    ? "none"
    : `${formatCurrency(lineItem.material_price_override)} ${
        !basisLabel || quotedBasis === "per_unit"
          ? "each"
          : quotedBasis === "per_line"
            ? "for the line"
            : `per ${basisLabel.toLowerCase()}`
      }`;

  return (
    <div className={`est-editor${lineError ? " est-editor--failed" : ""}`}>
      {lineError ? (
        <div className="est-linefail" role="alert">
          <p className="est-linefail__hd">
            This line did not price — it is contributing $0.00 to the job total.
          </p>
          <p className="est-linefail__msg">{lineError.message}</p>
          {lineError.detail ? (
            <details className="est-linefail__detail">
              <summary>Technical details</summary>
              <code>{lineError.detail}</code>
            </details>
          ) : null}
        </div>
      ) : null}

      {materialError ? (
        <div className="est-matfail" role="alert">
          <p className="est-matfail__hd">{MATERIAL_FAILURE_HEADLINE}</p>
          <p className="est-matfail__msg">{materialError.message}</p>
          {materialError.candidates.length ? (
            <ul className="est-matfail__candidates">
              {materialError.candidates
                .slice(0, MATERIAL_FAILURE_CANDIDATE_LIMIT)
                .map((candidate, candidateIndex) => (
                  <li key={candidate?.id ?? candidate?.name ?? candidateIndex}>
                    {candidate?.name ||
                      candidate?.label ||
                      `Candidate ${candidateIndex + 1}`}
                  </li>
                ))}
              {materialError.candidates.length >
              MATERIAL_FAILURE_CANDIDATE_LIMIT ? (
                <li className="est-matfail__more">
                  …and{" "}
                  {materialError.candidates.length -
                    MATERIAL_FAILURE_CANDIDATE_LIMIT}{" "}
                  more materials match
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>
      ) : null}

      {draftPending ? (
        <AiDraftBar
          draft={draft}
          onAccept={onAcceptDraft}
          onDiscard={onDiscardDraft}
          candidateState={candidateState}
          onRetryCandidate={onRetryCandidate}
        />
      ) : null}

      <div className="est-editor__hd">
        <div>
          {/* The eyebrow earns its line only when the line is unusual AND the
              H1 does not already carry the same words. A plain typed line says
              its product type in the select and the rail; an unnamed custom
              line says "Custom / one-off line" in the heading itself. */}
          {eyebrow && eyebrow !== title ? (
            <p className="wb-eyebrow">{eyebrow}</p>
          ) : null}
          <h1 className="wb-h1">{title}</h1>
          {subtitle ? <p className="wb-sub">{subtitle}</p> : null}
        </div>
        {modeToggle}
      </div>

      {customConfirmDialog}

      {/* Product mode starts with the category that determines the form and
          rate card. A first-time estimator should choose what the line is
          before being asked how to describe it. */}
      {!isCustom ? (
        <fieldset className="est-fieldset">
          <legend>Product</legend>
          <div className="est-fields">
            <div>
              <label htmlFor={`type-${lineIndex}`}>
                Product type{hasOverride ? " (optional)" : ""}
              </label>
              <select
                id={`type-${lineIndex}`}
                className="wb-input"
                value={isTyped ? lineItem.product_type_key : ""}
                aria-label={`Product for line item ${lineIndex + 1}`}
                onChange={(event) =>
                  update("product_type_key", event.target.value || null)
                }
              >
                {/* Selectable, not disabled, once a quoted price is on the
                    line: clearing the type is how an estimator turns a
                    shoehorned product line back into an honest quoted one. */}
                <option value="" disabled={!hasOverride}>
                  {hasOverride
                    ? "None — price from the quote"
                    : "Select product"}
                </option>
                {(availableProductTypes || []).map((type) => (
                  <option key={type.key} value={type.key}>
                    {type.display_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {canTrial ? (
            <div className="est-rt__model">
              <span className="est-rt__model-k" id={`model-${lineIndex}`}>
                Pricing
              </span>
              <div
                className="est-mode est-rt__model-seg"
                role="group"
                aria-labelledby={`model-${lineIndex}`}
              >
                <button
                  type="button"
                  className={`wb-plain est-mode__btn${isTrial ? " est-mode__btn--on" : ""}`}
                  aria-pressed={isTrial}
                  title="Andy's rafter-tail rules and linked customer tiers"
                  onClick={() =>
                    !isTrial && update("pricing_model", RAFTER_TAIL_TRIAL_MODEL)
                  }
                >
                  <span>{TRIAL_MODEL_LABEL}</span>
                </button>
                <button
                  type="button"
                  className={`wb-plain est-mode__btn${!isTrial ? " est-mode__btn--on" : ""}`}
                  aria-pressed={!isTrial}
                  title="The rafter-tail rate card used before the trial"
                  onClick={() => isTrial && update("pricing_model", null)}
                >
                  <span>{RATE_CARD_MODEL_LABEL}</span>
                </button>
              </div>
            </div>
          ) : null}
          {hasOverride && !isTrial ? (
            <p className="wb-hint">{STANDALONE_QUOTED_LINE_HINT}</p>
          ) : null}
          {isRetiredCatalogLine ? (
            <p className="est-alert" role="alert">
              Catalog products are retired — reselect a Product for this line.
            </p>
          ) : null}
        </fieldset>
      ) : null}

      {/* Custom lines still start with Identity; their name is the only stable
          description before the estimator chooses purchased/build inputs. */}
      <fieldset className="est-fieldset">
        <legend>Identity</legend>
        <div className="est-fields">
          <div>
            <label htmlFor={`name-${lineIndex}`}>Name</label>
            <Input
              id={`name-${lineIndex}`}
              value={lineItem.name || ""}
              placeholder="e.g. Outrigger 9/A5.10"
              onChange={(event) => update("name", event.target.value)}
              className="h-9"
            />
          </div>
        </div>
        <div>
          <label htmlFor={`description-${lineIndex}`}>Description</label>
          <textarea
            id={`description-${lineIndex}`}
            className="wb-input"
            rows={2}
            maxLength={500}
            value={lineItem.description || ""}
            placeholder="What the shop and the customer should read"
            onChange={(event) => update("description", event.target.value)}
          />
        </div>
      </fieldset>

      {isCustom ? (
        <CustomAssemblyFieldsets
          lineItem={lineItem}
          lineIndex={lineIndex}
          dispatch={dispatch}
          availableMaterials={availableMaterials}
          formData={formData}
          quantity={quantity}
        />
      ) : (
        <>
          <fieldset className="est-fieldset">
            <legend>Dimensions</legend>
            <div className="est-fields">
              {showDim("length") ? (
                <div>
                  <label htmlFor={`length-${lineIndex}`}>
                    {isTrial ? "Projection (L)" : "Length"}
                  </label>
                  <div className="est-suffix">
                    <NumberInput
                      id={`length-${lineIndex}`}
                      align="right"
                      step="0.5"
                      value={lineItem.length}
                      onValueChange={(value) => update("length", value ?? 0)}
                      aria-label={`Length for line item ${lineIndex + 1}`}
                      className="h-9"
                    />
                    <span>in</span>
                  </div>
                </div>
              ) : null}
              {showDim("width") ? (
                <div>
                  <label htmlFor={`width-${lineIndex}`}>
                    {isTrial ? "Width (W)" : "Width"}
                  </label>
                  <div className="est-suffix">
                    <NumberInput
                      id={`width-${lineIndex}`}
                      align="right"
                      step="0.5"
                      value={lineItem.width}
                      onValueChange={(value) => update("width", value ?? 0)}
                      aria-label={`Width for line item ${lineIndex + 1}`}
                      className="h-9"
                    />
                    <span>in</span>
                  </div>
                </div>
              ) : null}
              {showDim("height") ? (
                <div>
                  <label htmlFor={`height-${lineIndex}`}>
                    {isTrial ? "Height (H)" : "Height"}
                  </label>
                  <div className="est-suffix">
                    <NumberInput
                      id={`height-${lineIndex}`}
                      align="right"
                      step="0.5"
                      value={lineItem.height}
                      onValueChange={(value) => update("height", value ?? 0)}
                      aria-label={`Height for line item ${lineIndex + 1}`}
                      className="h-9"
                    />
                    <span>in</span>
                  </div>
                </div>
              ) : null}
              <div>
                <label htmlFor={`qty-${lineIndex}`}>Quantity</label>
                <div className="est-suffix">
                  <NumberInput
                    id={`qty-${lineIndex}`}
                    align="right"
                    step="1"
                    min={1}
                    value={lineItem.quantity}
                    emptyBlurValue={1}
                    onValueChange={(value) => update("quantity", value)}
                    aria-label={`Quantity for line item ${lineIndex + 1}`}
                    className="h-9"
                  />
                  <span>ea</span>
                </div>
              </div>
            </div>

            {isTrial ? (
              <p className="wb-hint">
                Finished inches. Over 9 in wide is a corbel, not a rafter tail.
              </p>
            ) : null}
            <Derived
              items={
                isTrial
                  ? []
                  : [
                      {
                        label: "Area each",
                        value:
                          sqft === null ? null : `${sqft.toFixed(2)} sq ft`,
                      },
                      {
                        label: "Total area",
                        value:
                          sqft === null
                            ? null
                            : `${(sqft * quantity).toFixed(2)} sq ft`,
                      },
                      { label: "Size band", value: band },
                    ]
              }
            />
          </fieldset>

          <fieldset className="est-fieldset">
            <legend>Material &amp; finish</legend>
            <div className="est-fields">
              <div>
                <label htmlFor={`class-${lineIndex}`}>Material class</label>
                <select
                  id={`class-${lineIndex}`}
                  className="wb-input"
                  value={lineItem.material_classification || ""}
                  aria-label={`Material class for line item ${lineIndex + 1}`}
                  onChange={(event) =>
                    update("material_classification", event.target.value || "")
                  }
                >
                  <option value="" disabled>
                    Select class
                  </option>
                  {classOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {isTrial ? null : (
                <div>
                  <label htmlFor={`sheet-${lineIndex}`}>
                    Specific sheet (optional)
                  </label>
                  <select
                    id={`sheet-${lineIndex}`}
                    className="wb-input"
                    value={lineItem.material_id ?? ""}
                    aria-label={`Specific sheet for line item ${lineIndex + 1}`}
                    onChange={(event) =>
                      update("material_id", event.target.value || null)
                    }
                  >
                    <option value="">Auto (card default thickness)</option>
                    {sheetOptions.map((material) => (
                      <option key={material.id} value={material.id}>
                        {material.name} ·{" "}
                        {formatCurrency(material.cost_per_sqft)}
                        /sqft
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label htmlFor={`finish-${lineIndex}`}>Finish</label>
                <select
                  id={`finish-${lineIndex}`}
                  className="wb-input"
                  value={
                    isTrial
                      ? trialFinishSelected(lineItem)
                        ? "island_finish"
                        : "prep_only"
                      : lineItem.finish_mode || ""
                  }
                  aria-label={`Finish for line item ${lineIndex + 1}`}
                  // What the chosen mode means rides on the control that
                  // sets it, not on a permanent paragraph below the fieldset.
                  title={isTrial ? undefined : finishHelper || undefined}
                  onChange={(event) =>
                    update("finish_mode", event.target.value || null)
                  }
                >
                  {isTrial ? null : (
                    <option value="" disabled>
                      Select finish
                    </option>
                  )}
                  {(isTrial ? TRIAL_FINISH_OPTIONS : FINISH_MODE_OPTIONS).map(
                    (option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ),
                  )}
                </select>
              </div>
            </div>

            {promptsForWaste && !isTrial ? (
              <div className="est-waste">
                <span className="est-waste__k">Material waste</span>
                <div
                  role="group"
                  aria-label={`Material waste for line item ${lineIndex + 1}`}
                  className="est-waste__chips"
                >
                  {WASTE_MODE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`wb-plain est-chip${
                        lineItem.waste_mode === option.value
                          ? " est-chip--on"
                          : ""
                      }`}
                      aria-pressed={lineItem.waste_mode === option.value}
                      onClick={() => update("waste_mode", option.value)}
                    >
                      {option.value === "default"
                        ? `Add default ${defaultWastePct}%`
                        : option.label}
                    </button>
                  ))}
                </div>
                {lineItem.waste_mode === "custom" ? (
                  <div className="est-waste__custom">
                    <NumberInput
                      align="center"
                      step="1"
                      min={0}
                      max={100}
                      value={lineItem.waste_custom_pct ?? ""}
                      onValueChange={(value) =>
                        update("waste_custom_pct", value)
                      }
                      aria-label={`Custom waste percent for line item ${lineIndex + 1}`}
                      className="h-8 w-16 text-[12px]"
                    />
                    <span>% waste</span>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Two sentences, because a quoted price makes the first one false.
                Without an override this says how the sheet was picked and that
                the line is priced with it. With one, the sheet did not price
                anything — so it is named as the catalog rate, how it was picked
                stops being interesting, and the caption says what did price the
                line instead. */}
            {resolution && !isTrial ? (
              <p className="wb-hint">
                {hasOverride ? `${CATALOG_RATE_LEAD} ` : "Priced with "}
                <b>{pricedWithMaterialLabel(resolution)}</b>
                {resolution.cost_per_sqft != null
                  ? ` · ${formatCurrency(resolution.cost_per_sqft)}/sq ft`
                  : ""}
                {hasOverride
                  ? ` ${QUOTED_PRICE_NOT_USED_SUFFIX}`
                  : describeMaterialResolution(resolution)
                    ? ` — ${describeMaterialResolution(resolution)}`
                    : ""}
              </p>
            ) : null}
            {/* Quoted price — Goal 2. Owner-confirmed: this lives on the
                typed line. Product type, rate card and shop labor are
                untouched; only the material dollars come from here. */}
            {isTrial ? null : (
              <Disclosure
                className="est-quoted"
                summary="Quoted price"
                meta={quotedMeta}
                open={hasOverride}
                forceOpen={hasOverride}
              >
                <div className="est-fields">
                  <div>
                    <label htmlFor={`quoted-amount-${lineIndex}`}>Amount</label>
                    <NumberInput
                      id={`quoted-amount-${lineIndex}`}
                      align="right"
                      step="0.01"
                      min={0}
                      value={lineItem.material_price_override ?? ""}
                      onValueChange={(value) =>
                        update("material_price_override", value)
                      }
                      aria-label={`Quoted price amount for line item ${lineIndex + 1}`}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <label htmlFor={`quoted-basis-${lineIndex}`}>Basis</label>
                    <select
                      id={`quoted-basis-${lineIndex}`}
                      className="wb-input"
                      value={
                        lineItem.material_price_override_basis || "per_unit"
                      }
                      aria-label={`Quoted price basis for line item ${lineIndex + 1}`}
                      onChange={(event) =>
                        update(
                          "material_price_override_basis",
                          event.target.value,
                        )
                      }
                    >
                      {MATERIAL_PRICE_OVERRIDE_BASIS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor={`quoted-note-${lineIndex}`}>Note</label>
                    <Input
                      id={`quoted-note-${lineIndex}`}
                      value={lineItem.material_price_override_note || ""}
                      placeholder="Vendor / quote ref"
                      maxLength={240}
                      onChange={(event) =>
                        update(
                          "material_price_override_note",
                          event.target.value,
                        )
                      }
                      className="h-9"
                    />
                  </div>
                </div>
                <p className="wb-note">
                  {isQuotedLine
                    ? STANDALONE_QUOTED_LINE_NOTE
                    : QUOTED_PRICE_HELPER}
                </p>
                <div className="est-quoted__acts">
                  <button
                    type="button"
                    className="wb-tbtn"
                    // A price observation is filed against a product family's
                    // rate card, so a line with no product type has nothing to
                    // file it against — the button says so rather than failing
                    // on click.
                    disabled={!hasOverride || !isTyped}
                    title={QUOTED_PRICE_FILE_HELPER}
                    onClick={() =>
                      hasOverride &&
                      isTyped &&
                      onFileQuotedPrice &&
                      onFileQuotedPrice(lineIndex)
                    }
                  >
                    Record quoted price
                  </button>
                  {hasOverride && !isTyped ? (
                    <p className="wb-hint">
                      Recording needs a product type — a quoted price is filed
                      against that family&apos;s rate card. The line still
                      prices from the quote without one.
                    </p>
                  ) : null}
                  {hasOverride ? (
                    <>
                      {quotedPriceFileState === "filed" ? (
                        <p className="est-draftbar__note" role="status">
                          {QUOTED_PRICE_FILED}
                        </p>
                      ) : null}
                      {quotedPriceFileState === "failed" ? (
                        <p
                          className="est-draftbar__note est-draftbar__note--warn"
                          role="status"
                        >
                          {QUOTED_PRICE_FILE_FAILED}
                        </p>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </Disclosure>
            )}
          </fieldset>

          {isTrial ? (
            <RafterTailTrialPanel
              result={result}
              jobTier={
                formData.client_pricing_tier ||
                formData.default_client_pricing_tier
              }
              withPaint={trialFinishSelected(lineItem)}
              components={(lineItem.children || []).map((child) =>
                childDisplayName(child, availableProductTypes),
              )}
              ratesSaved={Boolean(formData.linked_rates)}
              onRepriceToday={() =>
                dispatch({
                  type: "UPDATE_FIELD",
                  field: "linked_rates",
                  value: null,
                })
              }
              onRemoveComponent={(childIndex) =>
                dispatch({
                  type: "REMOVE_LINE_ITEM_CHILD",
                  index: lineIndex,
                  childIndex,
                })
              }
            />
          ) : (
            <>
              <fieldset className="est-fieldset">
                <legend>Labor</legend>
                <RateCardReadout
                  result={result}
                  lineItem={lineItem}
                  isAdmin={isAdmin}
                  isQuotedLine={isQuotedLine}
                />
                <LaborOverridePanel
                  lineItem={lineItem}
                  lineIndex={lineIndex}
                  dispatch={dispatch}
                  panelRef={laborPanelRef}
                />
              </fieldset>

              <ChildComponentsPanel
                lineItem={lineItem}
                lineIndex={lineIndex}
                dispatch={dispatch}
                availableProductTypes={availableProductTypes}
                availableMaterials={availableMaterials}
                result={result}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

/** Read-only view of the shared rate card. Editing it lives in Rate Cards. */
function RateCardReadout({ result, lineItem, isAdmin, isQuotedLine = false }) {
  const card = result?.rate_card_resolved || null;
  const rates = result?.labor_per_sqft_rates_used || null;
  const missing = Boolean(card?.labor_card_missing);
  const laborOverridden = laborOverrideApplied(result?.input_data || lineItem);
  const basisWord = result?.labor_basis_used
    ? LABOR_BASIS_WORDS[result.labor_basis_used] || result.labor_basis_used
    : "unit";
  // Hours for the LINE, taken from what was entered where that is what the
  // estimator typed, so this never contradicts the override panel by a
  // rounding hair the way an extended 4-decimal per-piece figure does.
  const lineHours = result ? getLineLaborHours(result) : null;
  const lineLaborCost = result
    ? getEngineLineTotal(result, "calculated_labor_cost")
    : null;

  if (!card && !rates) {
    return (
      <p className="wb-hint">
        {isQuotedLine
          ? "This line has no product type, so no rate card prices it — shop labor is whatever hours you enter below."
          : "Shop labor prices from the rate card once this line has a product type and dimensions."}
      </p>
    );
  }

  return (
    <div className="est-card">
      <div className="est-card__hd">
        <span>
          {missing
            ? "No rate card matches this line"
            : `Rate card · ${[card?.category, card?.band_label]
                .filter(Boolean)
                .join(" · ")}`}
        </span>
        {/* When the owner last signed off on these rates — the difference
            between "checked" and "sitting here for a year". */}
        <span>
          {[
            card?.card_status || null,
            card && !missing
              ? describeRateConfirmation(card.rate_confirmed_at)
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>
      {missing && !laborOverridden ? (
        <p className="est-alert" role="alert">
          No rate card matches this category and size — no shop labor was
          charged on this line.
        </p>
      ) : !missing ? (
        /* The per-department rates are provenance, not an input: the four
           hour fields the estimator edits are directly below, and the same
           table is open in the inspector. One click away, with the line's
           hour total on the closed row. */
        <Disclosure
          className="est-card__depts"
          summary="Department hours"
          /* No meta: the line's hour total is stated once, in the "Hours for
             the line" tile directly beneath this row. */
          meta={null}
        >
          <table className="est-card__table">
            <thead>
              <tr>
                <th scope="col">Department</th>
                <th scope="col">hr per {basisWord}</th>
                <th scope="col">Hours per piece</th>
              </tr>
            </thead>
            <tbody>
              {DEPARTMENT_ORDER.map((key) => {
                const rate = rates ? toNumberOrNull(rates[key]) : null;
                const hours = toNumberOrNull(result?.[`${key}_hours_per_unit`]);
                return (
                  <tr key={key}>
                    <th scope="row">{DEPARTMENT_LABELS[key]}</th>
                    <td className="wb-num">
                      {rate === null ? "—" : Number(rate.toFixed(5))}
                    </td>
                    <td className="wb-num">
                      {hours === null ? "—" : `${Number(hours.toFixed(4))} hr`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Disclosure>
      ) : null}
      {lineHours !== null || lineLaborCost !== null ? (
        <Derived
          items={[
            {
              label: "Hours for the line",
              value:
                lineHours === null
                  ? null
                  : `${Number(lineHours.toFixed(2))} hr`,
            },
            {
              label: "Shop labor for the line",
              value:
                lineLaborCost === null ? null : formatCurrency(lineLaborCost),
            },
          ]}
        />
      ) : null}
      {/* The shared-card warning is for the person who can act on it. An
          estimator cannot edit a rate card, so it rode on the admin link
          instead of on every line. */}
      {isAdmin ? (
        <div className="est-card__foot">
          <Link
            className="wb-tbtn wb-tbtn--edge"
            to="/admin/rate-cards"
            title="Rate cards are shared — changing one re-prices every job in the category."
          >
            Edit in Rate Cards
          </Link>
        </div>
      ) : null}
    </div>
  );
}

/** Custom and one-off lines price from explicit purchased/build inputs. */
function CustomAssemblyFieldsets({
  lineItem,
  lineIndex,
  dispatch,
  availableMaterials,
  formData,
  quantity,
}) {
  // Shop labor rate — the burden cost per employee-hour, and since the
  // labor-rate-type retirement (2026-08-18) the only rate a new estimate uses.
  // The two legacy branches survive because the engine still honors a
  // laborRateType saved on an older JobVersion, and this preview must show the
  // rate that estimate is actually costed at, not the one it should have been.
  const selectedLaborRate =
    formData.laborRateType === "island"
      ? formData.island_millwork_labor_rate
      : formData.laborRateType === "custom"
        ? formData.labor_custom_rate
        : formData.labor_burden_rate;

  return (
    <>
      <fieldset className="est-fieldset">
        <legend>Line quantity &amp; reference size</legend>
        <div className="est-fields">
          <div>
            <label htmlFor={`custom-qty-${lineIndex}`}>Quantity</label>
            <div className="est-suffix">
              <NumberInput
                id={`custom-qty-${lineIndex}`}
                align="right"
                step="1"
                min={1}
                value={lineItem.quantity}
                emptyBlurValue={1}
                onValueChange={(value) =>
                  dispatch({
                    type: "UPDATE_LINE_ITEM_FIELD",
                    index: lineIndex,
                    field: "quantity",
                    value,
                  })
                }
                aria-label={`Quantity for line item ${lineIndex + 1}`}
                className="h-9"
              />
              <span>ea</span>
            </div>
          </div>
        </div>

        {/* Size is a RECORD on a custom line, never a price input: components
            drive the cost. It exists so the quote, the shop and QuickBooks all
            say what was actually built — a custom line could not carry its own
            dimensions at all before. */}
        <div className="est-fields">
          {["length", "width", "height"].map((dim) => (
            <div key={dim}>
              <label htmlFor={`custom-${dim}-${lineIndex}`}>
                {dim === "length"
                  ? "Length"
                  : dim === "width"
                    ? "Width"
                    : "Height"}{" "}
                (optional)
              </label>
              <div className="est-suffix">
                <NumberInput
                  id={`custom-${dim}-${lineIndex}`}
                  align="right"
                  step="0.5"
                  value={lineItem[dim]}
                  onValueChange={(value) =>
                    dispatch({
                      type: "UPDATE_LINE_ITEM_FIELD",
                      index: lineIndex,
                      field: dim,
                      value: value ?? 0,
                    })
                  }
                  aria-label={`${dim} for line item ${lineIndex + 1}`}
                  className="h-9"
                />
                <span>in</span>
              </div>
            </div>
          ))}
        </div>
        <p className="wb-hint">
          <b>Quantity multiplies every cost below.</b> Dimensions are for the
          quote and shop only; they do not change the price.
        </p>
      </fieldset>

      <fieldset className="est-fieldset">
        <legend>How this line is costed</legend>
        <div className="est-components">
          <table>
            {(lineItem.assembly_components || []).length > 0 && (
              <colgroup>
                <col className="est-component-col--type" />
                <col className="est-component-col--name" />
                <col className="est-component-col--qty" />
                <col className="est-component-col--cost" />
                <col className="est-component-col--amount" />
                <col className="est-component-col--actions" />
              </colgroup>
            )}
            <tbody>
              {(lineItem.assembly_components || []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="est-components__empty">
                    <strong>What are you adding?</strong>
                    <span>
                      A purchased item — vendor, Amazon, hardware, or other
                      one-off costs — or materials and shop operations when
                      Island is building it.
                    </span>
                  </td>
                </tr>
              ) : null}
              {(lineItem.assembly_components || []).map((component, index) => (
                <ComponentSubRow
                  key={`component-${index}`}
                  component={component}
                  lineIndex={lineIndex}
                  componentIndex={index}
                  lineQuantity={quantity}
                  dispatch={dispatch}
                  availableMaterials={availableMaterials}
                  selectedLaborRate={selectedLaborRate}
                />
              ))}
              <CustomFooterRow
                lineItem={lineItem}
                lineIndex={lineIndex}
                dispatch={dispatch}
                columnCount={6}
              />
            </tbody>
          </table>
        </div>
      </fieldset>
    </>
  );
}

export default LineItemEditor;
